import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../hooks/useAuth';
import ForgotPassword from '../components/auth/ForgotPassword';

type AuthPath = 'choose' | 'super' | 'shul';

export default function LoginPage() {
  const { t } = useTranslation();
  usePageTitle(t('seo.login'), { noindex: true });
  const navigate = useNavigate();
  const { user, loading, signIn } = useAuth();

  const [path, setPath] = useState<AuthPath>('choose');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

  useEffect(() => {
    if (loading || !user || path === 'choose' || submitting) return;

    if (user.status === 'rejected') {
      setBlockedMessage(t('admin.applicationRejected'));
      return;
    }

    if (path === 'super') {
      if (user.role === 'superAdmin') {
        navigate('/super', { replace: true });
      } else {
        setBlockedMessage(t('common.notAuthorized'));
      }
      return;
    }

    if (path === 'shul') {
      if (user.role === 'editor' || user.role === 'superAdmin') {
        navigate('/admin', { replace: true });
      } else {
        setBlockedMessage(t('common.notAuthorized'));
      }
    }
  }, [user, loading, navigate, path, submitting, t]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setError(null);
    setBlockedMessage(null);
  };

  const goChoose = () => {
    setPath('choose');
    resetForm();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBlockedMessage(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch {
      setError(t('admin.loginError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (blockedMessage) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center space-y-4">
        <p className="text-ink">{blockedMessage}</p>
        <button
          type="button"
          onClick={goChoose}
          className="text-ink hover:underline text-sm"
        >
          {t('common.back')}
        </button>
        <div>
          <Link to="/" className="text-ink hover:underline text-sm">
            {t('nav.home')}
          </Link>
        </div>
      </div>
    );
  }

  const inputClass =
    'w-full px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink placeholder:text-ash focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink text-sm';
  const labelClass = 'block text-sm font-medium text-ink mb-1';
  const primaryBtnClass =
    'w-full py-2.5 rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 disabled:opacity-60 transition-opacity';
  const secondaryBtnClass =
    'w-full py-2.5 rounded-[10px] border border-ink text-ink text-sm font-medium hover:opacity-80 transition-opacity';

  if (path === 'choose') {
    return (
      <div className="max-w-md mx-auto px-4 py-10 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-xl font-semibold text-ink">{t('admin.authLandingTitle')}</h1>
          <p className="text-sm text-ash">{t('admin.authLandingSubtitle')}</p>
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              resetForm();
              setPath('super');
            }}
            className={primaryBtnClass}
          >
            {t('admin.loginAsSuperAdmin')}
          </button>
          <button
            type="button"
            onClick={() => {
              resetForm();
              setPath('shul');
            }}
            className={secondaryBtnClass}
          >
            {t('admin.shulAdminPath')}
          </button>
        </div>
      </div>
    );
  }

  if (path === 'super') {
    return (
      <div className="max-w-md mx-auto px-4 py-10">
        <div className="bg-paper rounded-[16px] border border-ink/10 p-6 space-y-5">
          <div className="space-y-1">
            <button
              type="button"
              onClick={goChoose}
              className="text-sm text-ink hover:underline"
            >
              {t('common.back')}
            </button>
            <h1 className="text-xl font-semibold text-ink text-center">
              {t('admin.loginAsSuperAdmin')}
            </h1>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="super-email" className={labelClass}>
                {t('admin.email')}
              </label>
              <input
                id="super-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="super-password" className={labelClass}>
                {t('admin.password')}
              </label>
              <input
                id="super-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
              />
            </div>
            {error && <p className="text-sm text-danger-600">{error}</p>}
            <button type="submit" disabled={submitting} className={primaryBtnClass}>
              {submitting ? t('common.loading') : t('admin.login')}
            </button>
          </form>
          <ForgotPassword email={email} />
        </div>
      </div>
    );
  }

  // path === 'shul'
  return (
    <div className="max-w-md mx-auto px-4 py-10">
      <div className="bg-paper rounded-[16px] border border-ink/10 p-6 space-y-5">
        <div className="space-y-1">
          <button
            type="button"
            onClick={goChoose}
            className="text-sm text-ink hover:underline"
          >
            {t('common.back')}
          </button>
          <h1 className="text-xl font-semibold text-ink text-center">
            {t('admin.shulAdminPath')}
          </h1>
        </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="shul-email" className={labelClass}>
                {t('admin.email')}
              </label>
              <input
                id="shul-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="shul-password" className={labelClass}>
                {t('admin.password')}
              </label>
              <input
                id="shul-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
              />
            </div>
            {error && <p className="text-sm text-danger-600">{error}</p>}
            <button type="submit" disabled={submitting} className={primaryBtnClass}>
              {submitting ? t('common.loading') : t('admin.login')}
            </button>
          </form>
          <ForgotPassword email={email} />

        <p className="text-sm text-ash text-center">
          {t('directory.admin.add.newHere')}{' '}
          <Link to="/directory/add" className="text-ink underline">
            {t('directory.admin.addListing')}
          </Link>
        </p>
      </div>
    </div>
  );
}
